import { useState } from "react";

import { PersonIcon } from "@vericus/cadmus-icons";
import {
  Avatar,
  Command,
  DropdownMenu,
  SelectButton,
  Text,
} from "@vericus/cadmus-ui";

/**
 * Searchable Single Select Item
 */
export interface AssigneeSingleSelectItem {
  value: string;
  label: string;
  description?: string;
}

interface AssigneeSelectProps {
  /**
   * All the available items to display and that are searchable
   */
  items: AssigneeSingleSelectItem[];
  /**
   * The placeholder text for when there is no search query inputted yet.
   */
  placeholder?: string;
  /** The currently selected item ID */
  selectedItemId?: string;
  /** Callback function when a new item is selected */
  onSelectItem: (selectedItemId: string[]) => void;
}

/** Component rendering a popover to search and select a single value */
export function AssigneeSelect(props: AssigneeSelectProps) {
  const { items, selectedItemId, onSelectItem } = props;

  const [open, setOpen] = useState<boolean>(false);

  return (
    <DropdownMenu.Root open={open} onOpenChange={(open) => setOpen(open)}>
      <DropdownMenu.Trigger asChild>
        <SelectButton icon={<PersonIcon size={24} label="persona" />}>
          <Text kind="bodyLine" as="div">
            {selectedItemId
              ? items.find((i) => i.value === selectedItemId)
                ? items.find((i) => i.value === selectedItemId)?.label
                : "Select"
              : "Select"}
          </Text>
        </SelectButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <Command.Root style={{ height: "100%", width: "100%" }}>
          <Command.List>
            <Command.Group>
              {items.map((item) => (
                <Command.RadioItem
                  key={item.value}
                  value={item.label}
                  onSelect={() => {
                    // If the clicked item is already selected, deselect it
                    if (selectedItemId === item.value) {
                      onSelectItem([]);
                    } else {
                      // Otherwise, select the new item
                      onSelectItem([item.value]);
                    }
                    setOpen(false);
                  }}
                  data-selected={
                    selectedItemId === item.value ? "true" : undefined
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 12,
                      padding: "8px",
                    }}
                  >
                    <Avatar
                      name={item.label}
                      size="md"
                      shade={items.map((i) => i.value).indexOf(item.value)}
                    />
                    {item.label}
                  </div>
                </Command.RadioItem>
              ))}
            </Command.Group>
          </Command.List>
        </Command.Root>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
